<script lang="ts">
  const X0 = 40;
  const X1 = 860;
  const P0 = 74;
  const P1 = 122;
  const NODE = 264;
  const M0 = 366;
  const M1 = 680;
  const DONE = 40;
  const MID = (P0 + P1) / 2;

  const agents = [
    { x: 122, name: 'Payables', held: false },
    { x: 286, name: 'Procurement', held: false },
    { x: 450, name: 'Sales ops', held: false },
    { x: 614, name: 'Support', held: false },
    { x: 778, name: 'Comms', held: true }
  ];

  const facts = ['Past decisions', 'Vendor history', 'Playbooks', 'Customer context', 'Pricing habits'];

  let seed = 7;
  const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647;

  const hatch: number[] = [];
  for (let y = M0 + 7; y < M1; y += 7) hatch.push(y);

  const roots = agents.flatMap((a) =>
    Array.from({ length: 15 }, () => {
      const sx = Math.min(X1 - 14, Math.max(X0 + 14, a.x + (rnd() - 0.5) * 300));
      const sy = M0 + 44 + rnd() * 200;
      return {
        sx: +sx.toFixed(1),
        sy: +sy.toFixed(1),
        d: `M${sx.toFixed(1)},${sy.toFixed(1)} C${sx.toFixed(1)},${(sy - 150).toFixed(1)} ${a.x},${NODE + 120} ${a.x},${NODE + 11}`
      };
    })
  );

  const arrow = (x: number, y1: number, y2: number) => {
    const dir = Math.sign(y2 - y1);
    return { x, y1, y2, head: `M${x - 7},${y2 - dir * 11} L${x},${y2} L${x + 7},${y2 - dir * 11}` };
  };
  const fast = arrow(920, P0 + 6, NODE - 14);
  const slow = arrow(920, M1 - 30, NODE + 26);
</script>

<figure class="pm">
  <div class="layers">
    <div class="layer" style="top: 6%">
      <h3>Policy</h3>
      <p>Rules owned by named people, enforced on every agent action.</p>
    </div>
    <div class="layer" style="top: 33%">
      <h3>Agents</h3>
      <p>Act for the company in finance, sales, support and comms.</p>
    </div>
    <div class="layer" style="top: 61%">
      <h3>Memory</h3>
      <p>Holds the company’s knowledge and habits. Agents draw on it, and nothing enforces it.</p>
    </div>
  </div>

  <div class="art">
    <svg viewBox="0 0 960 690" role="img" aria-label="Five agents sit between a thin policy layer above and a deep memory layer below. Four actions pass the policy layer and run. The Comms agent's action is held at the policy layer and traced to the owner of clause 5.">
      <rect x={X0} y={M0} width={X1 - X0} height={M1 - M0} fill="var(--bg-raised)" />
      {#each hatch as y}
        <line x1={X0} x2={X1} y1={y} y2={y} stroke="var(--border-subtle)" stroke-width="1" />
      {/each}
      <line x1={X0} x2={X1} y1={M0} y2={M0} stroke="var(--border)" stroke-width="1" />

      <g fill="none" stroke="var(--fg)" stroke-width="1" stroke-opacity="0.2">
        {#each roots as r}<path d={r.d} />{/each}
      </g>
      <g fill="var(--fg)" fill-opacity="0.45">
        {#each roots as r}<circle cx={r.sx} cy={r.sy} r="2.4" />{/each}
      </g>
      {#each facts as f, i}
        <text class="mono" x={X0 + (i + 0.5) * ((X1 - X0) / facts.length)} y={M1 - 18} text-anchor="middle" fill="var(--fg-muted)">{f.toUpperCase()}</text>
      {/each}

      <rect x={X0} y={P0} width={X1 - X0} height={P1 - P0} fill="color-mix(in srgb, var(--accent) 10%, transparent)" />
      <line x1={X0} x2={X1} y1={P0} y2={P0} stroke="var(--accent-hover)" stroke-width="1.25" />
      <line x1={X0} x2={X1} y1={P1} y2={P1} stroke="var(--accent-hover)" stroke-width="1.25" />

      {#each agents as a, i}
        {#if a.held}
          <line x1={a.x} x2={a.x} y1={NODE - 10} y2={P1 + 14} stroke="var(--accent-red)" stroke-width="1.75" />
          <line x1={a.x - 13} x2={a.x + 13} y1={P1 + 14} y2={P1 + 14} stroke="var(--accent-red)" stroke-width="2.5" />
          <text class="mono strong" x={a.x + 20} y={P1 + 19} fill="var(--accent-red)">HELD</text>
          <line x1={a.x} x2={a.x} y1={P1 + 10} y2={DONE - 22} stroke="var(--accent-hover)" stroke-width="1.25" stroke-dasharray="3 4" />
          <circle cx={a.x} cy={DONE - 22} r="4.5" fill="none" stroke="var(--accent-hover)" stroke-width="1.5" />
          <text class="owner" x={a.x - 16} y={DONE - 16} text-anchor="end" fill="var(--fg)"><tspan font-weight="600">M. Chen, Head of Comms</tspan><tspan fill="var(--fg-muted)">&nbsp;· owner of §5</tspan></text>
        {:else}
          <line x1={a.x} x2={a.x} y1={NODE - 10} y2={DONE} stroke="var(--fg)" stroke-width="1.25" />
          <rect x={a.x - 5} y={MID - 5} width="10" height="10" fill="var(--accent)" />
          <circle cx={a.x} cy={DONE} r="4.5" fill="var(--fg)" />
        {/if}
        <text class="mono strong" x={a.x + 14} y={MID + 5} fill="var(--accent-hover)">§{i + 1}</text>
        <rect x={a.x - 10} y={NODE - 10} width="20" height="20" fill="var(--bg-deeper)" stroke={a.held ? 'var(--accent-red)' : 'var(--fg)'} stroke-width="1.5" />
        <text class="mono" x={a.x + 18} y={NODE - 18} fill="var(--fg)">{a.name.toUpperCase()}</text>
      {/each}
      <text class="mono" x={agents[0].x + 14} y={DONE + 5} fill="var(--fg-muted)">EXECUTED</text>

      <line x1={fast.x} x2={fast.x} y1={fast.y1} y2={fast.y2} stroke="var(--accent-hover)" stroke-width="1.5" />
      <path d={fast.head} fill="none" stroke="var(--accent-hover)" stroke-width="1.5" />
      <line x1={slow.x} x2={slow.x} y1={slow.y1} y2={slow.y2} stroke="var(--fg-muted)" stroke-width="1.5" />
      <path d={slow.head} fill="none" stroke="var(--fg-muted)" stroke-width="1.5" />
    </svg>
  </div>

  <div class="loops">
    <div class="loop" style="top: 9%">
      <p class="k gold">Real-time loop</p>
      <p>Policy checks each action before it runs. A held action goes to the person who owns the rule.</p>
    </div>
    <div class="loop" style="top: 58%">
      <p class="k">Long loop</p>
      <p>Memory builds up from the company’s work over months. It gives agents the context to act the way the company does.</p>
    </div>
  </div>

  <figcaption>Illustrative. Agent names, clauses and the owner are examples.</figcaption>
</figure>

<style>
  .pm { display: grid; grid-template-columns: minmax(190px, 240px) minmax(0, 1fr) minmax(190px, 250px); gap: 28px; margin: 0; }
  .layers, .loops { position: relative; }
  .layer, .loop { position: absolute; left: 0; right: 0; }
  .layer h3 { font: 500 1.9rem/1.08 var(--font-serif); font-variation-settings: 'opsz' 11; letter-spacing: 0; margin-bottom: 8px; }
  .layer p, .loop p { font: 0.95rem/1.5 var(--font-body); color: var(--fg-muted); }
  .loop .k { font: 600 0.8rem var(--font-ui); letter-spacing: 0.08em; text-transform: uppercase; color: var(--fg-muted); margin-bottom: 8px; }
  .loop .k.gold { color: var(--accent-hover); }
  svg { display: block; width: 100%; height: auto; overflow: visible; }
  svg .mono { font: 500 13px var(--font-mono); letter-spacing: 0.14em; }
  svg .strong { font-weight: 600; font-size: 14px; }
  svg .owner { font: 18px var(--font-body); }
  figcaption { grid-column: 1 / -1; font: 0.8rem var(--font-body); color: var(--fg-muted); }

  @media (max-width: 1000px) {
    .pm { grid-template-columns: 1fr; gap: 20px; }
    .layers, .loops { display: grid; gap: 18px; }
    .loops { grid-template-columns: 1fr 1fr; }
    .layer, .loop { position: static; }
    .layers { order: 2; grid-template-columns: repeat(3, 1fr); }
    .loops { order: 3; }
    .art { order: 1; overflow-x: auto; direction: rtl; }
    .art svg { min-width: 620px; direction: ltr; }
    figcaption { order: 4; }
  }
  @media (max-width: 640px) {
    .layers, .loops { grid-template-columns: 1fr; }
  }
</style>
